/**
 * Grouping-key registry (CONTRACT §4) — the one vocabulary the triage
 * queue, hotspot map, quality gate and ownership rules group canonical
 * findings by. A key is `<kind>:<parts joined by ":">`; route keys are
 * the last part and may themselves contain ":" (`/word/:slug`).
 */
import { pathOfUrl, routeKeyForPath } from "./hotspots/route-patterns.js";

export const GROUPING_KEY_KINDS = [
  "capability",
  "outline",
  "scenario",
  "persona",
  "human_test",
  "route",
] as const;
export type GroupingKeyKind = (typeof GROUPING_KEY_KINDS)[number];

export type GroupingKey =
  | { kind: "capability"; capabilityId: string }
  | { kind: "outline"; outlineId: string }
  | { kind: "scenario"; scenarioId: string }
  | { kind: "persona"; personaId: string }
  | { kind: "human_test"; outlineId: string; humanTestId: string }
  | { kind: "route"; routeKey: string };

export function formatGroupingKey(key: GroupingKey): string {
  switch (key.kind) {
    case "capability":
      return `capability:${key.capabilityId}`;
    case "outline":
      return `outline:${key.outlineId}`;
    case "scenario":
      return `scenario:${key.scenarioId}`;
    case "persona":
      return `persona:${key.personaId}`;
    case "human_test":
      return `human_test:${key.outlineId}:${key.humanTestId}`;
    case "route":
      return `route:${key.routeKey}`;
  }
}

export function parseGroupingKey(value: string): GroupingKey | null {
  const i = value.indexOf(":");
  if (i <= 0) return null;
  const kind = value.slice(0, i);
  const rest = value.slice(i + 1);
  if (!rest) return null;
  switch (kind) {
    case "capability":
      return { kind, capabilityId: rest };
    case "outline":
      return { kind, outlineId: rest };
    case "scenario":
      return { kind, scenarioId: rest };
    case "persona":
      return { kind, personaId: rest };
    case "human_test": {
      const j = rest.indexOf(":");
      if (j <= 0 || j === rest.length - 1) return null;
      return {
        kind,
        outlineId: rest.slice(0, j),
        humanTestId: rest.slice(j + 1),
      };
    }
    case "route":
      return { kind, routeKey: rest };
    default:
      return null;
  }
}

/**
 * Route key of a page URL: the first matching project route pattern
 * (`/:lang/word/:slug`), else the decoded path with volatile segments
 * templated (`templateUrl`), without origin, query or fragment. Null
 * for missing or unparseable URLs.
 */
export function routeKeyForUrl(
  url: string | null | undefined,
  patterns: readonly string[] = [],
): string | null {
  const path = pathOfUrl(url);
  return path === null ? null : routeKeyForPath(path, patterns);
}
